import logger from '@/utils/client-logger';
import { getEventAdapter } from './adapters/eventAdapters';
import { ProviderRegistry } from './providerRegistry';
import { Provider, ProviderType, TrackingEvent } from './types';
import { generateEventId } from './utils/eventIdGenerator';

/** 单个 Provider 释放全局初始化等待的最长时间。 */
const PROVIDER_INIT_TIMEOUT_MS = 3000;

/** Sensors 独占网络优先级后启动非关键营销 Provider 的最长窗口。 */
const SENSORS_PRIORITY_WINDOW_MS = 1000;

/** 单个 Provider 最多保留的待发送事件数，防止永久 pending 导致内存无界增长。 */
const MAX_PROVIDER_EVENT_QUEUE_SIZE = 1000;

/** 必须首批启动的埋点与合规 Provider。 */
const IMMEDIATE_PROVIDER_TYPES = new Set<ProviderType>([
  ProviderType.SENSORS,
  ProviderType.ONETRUST,
  ProviderType.CONSENT_MANAGER,
]);

/** 尚未解析到目标 Provider 时保留的原始追踪调用。 */
type QueuedTrackCall = {
  event: TrackingEvent;
  providers?: ProviderType[];
  adaptionData?: unknown;
  adapterType?: string;
};

/** Provider 在 ReportingCenter 内部的初始化状态。 */
type ProviderInitStatus = 'registered' | 'initializing' | 'blocked' | 'ready' | 'failed';

/** 暴露可恢复身份门禁状态的 Provider 契约。 */
interface IdentityBlockedProvider extends Provider {
  isIdentityBlocked: () => boolean;
}

/** 等待 Sensors 身份恢复后再发送的 Snowplow 镜像事件。 */
type QueuedSensorsMirrorEvent = {
  provider: Provider;
  event: TrackingEvent;
};

/** 判断 Provider 是否暴露可恢复身份门禁状态。 */
function isIdentityBlockedProvider(provider: Provider): provider is IdentityBlockedProvider {
  return 'isIdentityBlocked' in provider && typeof provider.isIdentityBlocked === 'function';
}

export class ReportingCenter {
  private static instance: ReportingCenter;

  static getInstance(): ReportingCenter {
    if (!ReportingCenter.instance) {
      ReportingCenter.instance = new ReportingCenter();
      // 暴露日志控制方法到window对象
      if (typeof window !== 'undefined') {
        (window as any).enableReportCenterLog = ReportingCenter.instance.enableLogging.bind(
          ReportingCenter.instance,
        );
        (window as any).disableReportCenterLog = ReportingCenter.instance.disableLogging.bind(
          ReportingCenter.instance,
        );
        (window as any).getReportCenterLogStatus = ReportingCenter.instance.getLoggingStatus.bind(
          ReportingCenter.instance,
        );
        (window as any).listReportCenterProviders = ReportingCenter.instance.listProviders.bind(
          ReportingCenter.instance,
        );
      }
    }
    return ReportingCenter.instance;
  }

  private registry: ProviderRegistry;
  private initialized = false;
  /** 所有并发 initialize 调用共享的初始化过程。 */
  private initializationPromise?: Promise<void>;
  /** 尚未解析到目标 Provider 的原始事件队列。 */
  private eventQueue: QueuedTrackCall[] = [];
  /** 各 Provider 的独立初始化状态。 */
  private providerInitStatus = new Map<ProviderType, ProviderInitStatus>();
  /** 各 Provider 初始化期间的独立事件队列。 */
  private providerEventQueues = new Map<ProviderType, TrackingEvent[]>();
  /** 已记录过容量告警的 Provider，避免永久 pending 时重复刷日志。 */
  private providerQueueOverflowWarned = new Set<ProviderType>();
  /** 因 Sensors 身份失败而等待的 Snowplow 镜像事件。 */
  private sensorsMirrorEventQueue: QueuedSensorsMirrorEvent[] = [];
  /** Sensors 镜像队列是否已记录容量告警。 */
  private sensorsMirrorQueueOverflowWarned = false;
  /** Sensors 终态失败后丢弃镜像事件是否已记录聚合告警。 */
  private sensorsTerminalFailureDropWarned = false;

  private constructor() {
    this.registry = new ProviderRegistry();
  }

  /** 幂等启动 Provider 初始化，并复用首次调用创建的 Promise。 */
  initialize(): Promise<void> {
    if (!this.initializationPromise) {
      this.initializationPromise = this.runInitialization();
    }

    return this.initializationPromise;
  }

  /** 首批启动 Sensors 与合规 Provider，再按 Sensors 状态或优先窗口启动其余 Provider。 */
  private async runInitialization(): Promise<void> {
    this.promoteRawQueueBeforeProviderQueues();
    const providers = this.registry.getEnabledProviders();
    const immediateProviders = providers.filter((provider) =>
      IMMEDIATE_PROVIDER_TYPES.has(provider.type),
    );
    const deferredProviders = providers.filter(
      (provider) => !IMMEDIATE_PROVIDER_TYPES.has(provider.type),
    );
    const immediatePromiseMap = new Map(
      immediateProviders.map((provider) => [provider.type, this.initializeProvider(provider)]),
    );
    const sensorsPromise = immediatePromiseMap.get(ProviderType.SENSORS) ?? Promise.resolve(false);

    await Promise.race([sensorsPromise, this.delay(SENSORS_PRIORITY_WINDOW_MS)]);
    const deferredPromises = deferredProviders.map((provider) => this.initializeProvider(provider));

    await Promise.allSettled([...Array.from(immediatePromiseMap.values()), ...deferredPromises]);

    // Process any events that were queued during initialization
    this.initialized = true;
    this.processQueue();
  }

  /** 先分配注册前原始事件，再拼接注册后事件，保持两类队列的全局 FIFO。 */
  private promoteRawQueueBeforeProviderQueues(): void {
    if (this.eventQueue.length === 0) return;

    const laterProviderQueues = this.providerEventQueues;
    this.providerEventQueues = new Map<ProviderType, TrackingEvent[]>();
    this.processQueue();

    laterProviderQueues.forEach((laterEvents, providerType) => {
      laterEvents.forEach((event) => this.enqueueProviderEvent(providerType, event));
    });
  }

  /** 等待指定毫秒数后释放，用于控制 Sensors 网络优先窗口。 */
  private delay(milliseconds: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }

  /** 注册 Provider，并记录其尚未初始化的初始状态。 */
  registerProvider(provider: Provider): void {
    const existingProvider = this.registry.getProvider(provider.type);
    if (this.initializationPromise && existingProvider) {
      logger.warn(`⚠️ Provider ${provider.type} registration ignored after initialization started`);
      return;
    }

    this.registry.registerProvider(provider);
    this.providerInitStatus.set(provider.type, 'registered');
  }

  /**
   * 追踪事件
   * @param event 事件数据 (可以是原始 TrackingEvent 或业务数据)
   * @param specificProviders 指定的 Provider 类型数组
   * @param adapterType 适配器类型，用于触发对应适配器 (如 'checkout.initiate')
   */
  track(
    event: TrackingEvent,
    specificProviders?: ProviderType[],
    adaptionData?: unknown,
    adapterType?: string,
  ): void {
    const providers = this.resolveTargetProviders(specificProviders);

    // 初始化前尚未注册目标 Provider 时，保留原始调用等待初始化阶段重新解析。
    if (providers.length === 0) {
      if (!this.initialized) {
        this.eventQueue.push({ event, providers: specificProviders, adaptionData, adapterType });
      } else {
        logger.warn(
          `⚠️ No enabled provider found for event ${event?.eventName ?? 'unknown'}; event dropped`,
        );
      }
      return;
    }

    // 如果有 adapterType，必须使用适配器
    if (adapterType) {
      const adapter = getEventAdapter(adapterType);
      if (!adapter) {
        logger.error(`❌ No adapter found for adapter type: ${adapterType}`);
        return;
      }

      // 生成统一的事件 ID
      const eventId = generateEventId();
      logger.log(`🎯 Using event adapter for type: ${adapterType}`, {
        originalData: event,
        targetProviders: providers.map((p) => p.type),
        eventId,
      });

      // 为每个 Provider 使用适配器生成适配后的事件
      providers.forEach((provider) => {
        const adaptedEvent = adapter(adaptionData, provider.type, eventId);
        if (!adaptedEvent) {
          logger.error(
            `❌ Adapter failed to generate event for provider ${provider.type} with adapter type: ${adapterType}`,
          );
          return;
        }

        logger.log(`📤 Sending adapted event to ${provider.type}:`, adaptedEvent);
        const waitForSensorsIdentity = this.shouldGateSensorsMirror(provider);
        if (Array.isArray(adaptedEvent)) {
          adaptedEvent.forEach((evt) =>
            this.trackOrQueueProviderEvent(provider, evt, waitForSensorsIdentity),
          );
        } else {
          this.trackOrQueueProviderEvent(provider, adaptedEvent, waitForSensorsIdentity);
        }
      });
    } else {
      // 没有 adapterType，使用传统方式处理
      this.trackWithoutAdapter(event as TrackingEvent, providers);
    }
  }

  /**
   * 启用指定Provider的日志
   * @param providerTypes Provider类型数组，为空数组或undefined时启用所有Provider的日志
   */
  enableLogging(providerTypes?: string[]): void {
    const providers = this.getTargetProviders(providerTypes);

    providers.forEach((provider) => {
      if ('enableLog' in provider && typeof provider.enableLog === 'function') {
        (provider as any).enableLog();
        logger.log(`✅ Enabled logging for ${provider.type} provider`);
      } else {
        logger.warn(`⚠️ Provider ${provider.type} does not support logging control`);
      }
    });

    logger.log('📊 Current logging status:', this.getLoggingStatus());
  }

  /**
   * 禁用指定Provider的日志
   * @param providerTypes Provider类型数组，为空数组或undefined时禁用所有Provider的日志
   */
  disableLogging(providerTypes?: string[]): void {
    const providers = this.getTargetProviders(providerTypes);

    providers.forEach((provider) => {
      if ('disableLog' in provider && typeof provider.disableLog === 'function') {
        (provider as any).disableLog();
        logger.log(`❌ Disabled logging for ${provider.type} provider`);
      } else {
        logger.warn(`⚠️ Provider ${provider.type} does not support logging control`);
      }
    });

    logger.log('📊 Current logging status:', this.getLoggingStatus());
  }

  /**
   * 获取当前所有Provider的日志状态
   */
  getLoggingStatus(): Record<string, boolean | 'not_supported'> {
    const providers = this.registry.getAllProviders();
    const status: Record<string, boolean | 'not_supported'> = {};

    providers.forEach((provider) => {
      if ('loggingEnabled' in provider) {
        status[provider.type] = (provider as any).loggingEnabled;
      } else {
        status[provider.type] = 'not_supported';
      }
    });

    return status;
  }

  /**
   * 列出所有已注册的Provider
   */
  listProviders(): string[] {
    return this.registry.getAllProviders().map((provider) => provider.type);
  }

  /**
   * 获取特定类型的Provider实例
   */
  getProvider(type: ProviderType): Provider | undefined {
    return this.registry.getProvider(type);
  }

  /**
   * 恢复当前注册 Provider 的 ready 状态，并刷新或清理其阻断期间的独立队列。
   */
  handleProviderRecovery(provider: Provider, discardQueuedEvents: boolean): void {
    const registeredProvider = this.registry.getProvider(provider.type);
    if (registeredProvider !== provider || !provider.isInitialized()) {
      logger.warn(`⚠️ Ignored invalid recovery for provider ${provider.type}`);
      return;
    }

    this.providerInitStatus.set(provider.type, 'ready');
    if (provider.type === ProviderType.SENSORS) {
      this.sensorsTerminalFailureDropWarned = false;
    }
    if (discardQueuedEvents) {
      this.clearProviderQueue(provider.type);
      if (provider.type === ProviderType.SENSORS) {
        this.clearSensorsMirrorQueue();
      }
      return;
    }

    this.flushProviderQueue(provider);
    if (provider.type === ProviderType.SENSORS) {
      this.flushSensorsMirrorQueue();
    }
  }

  /** 标记当前注册 Provider 的身份门禁，并按需丢弃上一身份周期的队列。 */
  handleProviderIdentityBlocked(provider: Provider, discardQueuedEvents: boolean): void {
    const registeredProvider = this.registry.getProvider(provider.type);
    if (registeredProvider !== provider) {
      logger.warn(`⚠️ Ignored invalid identity block for provider ${provider.type}`);
      return;
    }

    this.providerInitStatus.set(provider.type, 'blocked');
    if (!discardQueuedEvents) return;

    this.clearProviderQueue(provider.type);
    if (provider.type === ProviderType.SENSORS) {
      this.clearSensorsMirrorQueue();
    }
  }

  /**
   * 不使用适配器的传统追踪方式
   */
  private trackWithoutAdapter(event: TrackingEvent, providers: Provider[]): void {
    const sensorsEvent = this.withSensorsEventId(event, providers);
    providers.forEach((provider) => {
      const eventForProvider =
        provider.type === ProviderType.SENSORS || provider.type === ProviderType.SNOWPLOW
          ? sensorsEvent
          : event;

      this.trackOrQueueProviderEvent(
        provider,
        eventForProvider,
        this.shouldGateSensorsMirror(provider),
      );
    });
  }

  /** 初始化单个 Provider，并用非取消式超时限制全局等待。 */
  private initializeProvider(provider: Provider): Promise<boolean> {
    this.providerInitStatus.set(provider.type, 'initializing');

    const initPromise = provider
      .init()
      .then((result) => {
        if (result) {
          if (provider.isInitialized()) {
            this.providerInitStatus.set(provider.type, 'ready');
            this.flushProviderQueue(provider);
            if (provider.type === ProviderType.SENSORS) {
              this.sensorsTerminalFailureDropWarned = false;
              this.flushSensorsMirrorQueue();
            }
            return true;
          }

          this.providerInitStatus.set(provider.type, 'blocked');
          return false;
        }

        this.providerInitStatus.set(provider.type, 'failed');
        this.clearProviderQueue(provider.type);
        if (provider.type === ProviderType.SENSORS) {
          this.clearSensorsMirrorQueue();
        }
        return false;
      })
      .catch((error: unknown) => {
        logger.error(`❌ Provider ${provider.type} initialization failed:`, error);
        this.providerInitStatus.set(provider.type, 'failed');
        this.clearProviderQueue(provider.type);
        if (provider.type === ProviderType.SENSORS) {
          this.clearSensorsMirrorQueue();
        }
        return false;
      });

    const timeoutPromise = this.createProviderTimeout(provider.type);
    return Promise.race([initPromise, timeoutPromise]);
  }

  /** 超时仅释放等待，不取消原初始化，也不修改 Provider 状态或队列。 */
  private createProviderTimeout(providerType: ProviderType): Promise<boolean> {
    return new Promise((resolve) => {
      setTimeout(() => {
        if (this.providerInitStatus.get(providerType) === 'initializing') {
          logger.warn(
            `⏱️ Provider ${providerType} initialization timed out after ${PROVIDER_INIT_TIMEOUT_MS}ms`,
          );
        }

        resolve(false);
      }, PROVIDER_INIT_TIMEOUT_MS);
    });
  }

  /** Provider ready 时立即发送，否则保存独立事件快照。 */
  private trackOrQueueProviderEvent(
    provider: Provider,
    event: TrackingEvent,
    waitForSensorsIdentity = false,
  ): void {
    if (waitForSensorsIdentity) {
      if (this.providerInitStatus.get(ProviderType.SENSORS) === 'failed') {
        if (!this.sensorsTerminalFailureDropWarned) {
          this.sensorsTerminalFailureDropWarned = true;
          logger.warn(
            '⚠️ Sensors initialization failed; dropping Snowplow mirror events blocked on Sensors identity',
          );
        }
        return;
      }

      this.enqueueSensorsMirrorEvent(provider, event);
      return;
    }

    if (provider.isInitialized()) {
      this.trackProviderEvent(provider, event);
      return;
    }

    if (this.providerInitStatus.get(provider.type) === 'failed') {
      logger.warn(`⚠️ Provider ${provider.type} is not initialized`);
      return;
    }

    this.enqueueProviderEvent(provider.type, event);
  }

  /** 判断 Snowplow 是否必须等待同一事件的 Sensors 身份恢复。 */
  private shouldGateSensorsMirror(provider: Provider): boolean {
    if (provider.type !== ProviderType.SNOWPLOW) return false;

    const sensorsProvider = this.registry.getProvider(ProviderType.SENSORS);
    return Boolean(
      sensorsProvider &&
        sensorsProvider.isEnabled &&
        isIdentityBlockedProvider(sensorsProvider) &&
        sensorsProvider.isIdentityBlocked(),
    );
  }

  /** 将事件写入有界 Provider 队列，满载时丢弃最旧事件并只告警一次。 */
  private enqueueProviderEvent(providerType: ProviderType, event: TrackingEvent): void {
    const queue = this.providerEventQueues.get(providerType) ?? [];
    if (queue.length >= MAX_PROVIDER_EVENT_QUEUE_SIZE) {
      queue.shift();
      if (!this.providerQueueOverflowWarned.has(providerType)) {
        this.providerQueueOverflowWarned.add(providerType);
        logger.warn(
          `⚠️ Provider ${providerType} event queue reached ${MAX_PROVIDER_EVENT_QUEUE_SIZE}; dropping oldest events`,
        );
      }
    }
    queue.push(this.cloneEventForQueue(event));
    this.providerEventQueues.set(providerType, queue);
  }

  /** 将 Snowplow 镜像写入 Sensors 身份门禁专用有界队列。 */
  private enqueueSensorsMirrorEvent(provider: Provider, event: TrackingEvent): void {
    if (this.sensorsMirrorEventQueue.length >= MAX_PROVIDER_EVENT_QUEUE_SIZE) {
      this.sensorsMirrorEventQueue.shift();
      if (!this.sensorsMirrorQueueOverflowWarned) {
        this.sensorsMirrorQueueOverflowWarned = true;
        logger.warn(
          `⚠️ Sensors mirror event queue reached ${MAX_PROVIDER_EVENT_QUEUE_SIZE}; dropping oldest events`,
        );
      }
    }
    this.sensorsMirrorEventQueue.push({
      provider,
      event: this.cloneEventForQueue(event),
    });
  }

  /** 删除后再发送，避免重入重复刷新。 */
  private flushProviderQueue(provider: Provider): void {
    const queue = this.providerEventQueues.get(provider.type) ?? [];
    this.providerEventQueues.delete(provider.type);
    this.providerQueueOverflowWarned.delete(provider.type);
    queue.forEach((event) => this.trackProviderEvent(provider, event));
  }

  /** 清理指定 Provider 的待发送事件。 */
  private clearProviderQueue(providerType: ProviderType): void {
    this.providerEventQueues.delete(providerType);
    this.providerQueueOverflowWarned.delete(providerType);
  }

  /** Sensors 同身份恢复后按 FIFO 继续处理被门禁的 Snowplow 镜像。 */
  private flushSensorsMirrorQueue(): void {
    const queue = this.sensorsMirrorEventQueue;
    this.sensorsMirrorEventQueue = [];
    this.sensorsMirrorQueueOverflowWarned = false;
    queue.forEach(({ provider, event }) => this.trackOrQueueProviderEvent(provider, event));
  }

  /** Sensors 切换身份恢复时丢弃旧身份对应的 Snowplow 镜像。 */
  private clearSensorsMirrorQueue(): void {
    this.sensorsMirrorEventQueue = [];
    this.sensorsMirrorQueueOverflowWarned = false;
  }

  /** 固定事件入队时的 properties 和 options。 */
  private cloneEventForQueue(event: TrackingEvent): TrackingEvent {
    return {
      ...event,
      properties: this.cloneQueuePayload(event.properties),
      options: this.cloneQueuePayload(event.options),
    };
  }

  /** 深拷贝事件载荷，并在运行环境不支持时逐级降级。 */
  private cloneQueuePayload<T>(payload: T): T {
    if (payload === undefined || payload === null) {
      return payload;
    }

    if (typeof structuredClone === 'function') {
      try {
        return structuredClone(payload);
      } catch {
        // 继续使用 JSON 复制处理 structuredClone 不支持的载荷。
      }
    }

    try {
      return JSON.parse(JSON.stringify(payload)) as T;
    } catch {
      return payload;
    }
  }

  /**
   * 当本次目标包含 Sensors 时，为神策及其 Snowplow 镜像事件补齐同一个 eventId。
   */
  private withSensorsEventId(event: TrackingEvent, providers: Provider[]): TrackingEvent {
    const hasSensors = providers.some((provider) => provider.type === ProviderType.SENSORS);
    if (!hasSensors || event.eventId) {
      return event;
    }

    return {
      ...event,
      eventId: generateEventId(),
    };
  }

  private resolveTargetProviders(specificProviders?: ProviderType[]): Provider[] {
    let providers = this.registry.getEnabledProviders();

    if (specificProviders && specificProviders.length > 0) {
      providers = providers.filter((provider) => specificProviders.includes(provider.type));
    }

    return this.withSensorsSnowplowMirror(providers);
  }

  /**
   * 当目标包含 Sensors 时，自动纳入 Snowplow，并保证调用顺序为 Sensors -> Snowplow。
   */
  private withSensorsSnowplowMirror(providers: Provider[]): Provider[] {
    const hasSensors = providers.some((provider) => provider.type === ProviderType.SENSORS);
    const hasSnowplow = providers.some((provider) => provider.type === ProviderType.SNOWPLOW);

    let resolvedProviders = providers;
    if (hasSensors && !hasSnowplow) {
      const snowplowProvider = this.registry.getProvider(ProviderType.SNOWPLOW);
      if (snowplowProvider?.isEnabled) {
        resolvedProviders = [...providers, snowplowProvider];
      }
    }

    return this.orderSensorsSnowplowFirst(resolvedProviders);
  }

  private orderSensorsSnowplowFirst(providers: Provider[]): Provider[] {
    const sensorsProvider = providers.find((provider) => provider.type === ProviderType.SENSORS);
    if (!sensorsProvider) {
      return providers;
    }

    const snowplowProvider = providers.find((provider) => provider.type === ProviderType.SNOWPLOW);
    const remainingProviders = providers.filter(
      (provider) =>
        provider.type !== ProviderType.SENSORS && provider.type !== ProviderType.SNOWPLOW,
    );

    if (!snowplowProvider) {
      return [sensorsProvider, ...remainingProviders];
    }

    return [sensorsProvider, snowplowProvider, ...remainingProviders];
  }

  private trackProviderEvent(provider: Provider, event: TrackingEvent): void {
    if (!provider.isInitialized()) {
      logger.warn(`⚠️ Provider ${provider.type} is not initialized`);
      return;
    }

    try {
      provider.track(event);
    } catch (error) {
      logger.error(`❌ Provider ${provider.type} tracking failed:`, error);
    }
  }

  /** 重新解析并处理尚未找到目标 Provider 的原始事件。 */
  private processQueue(): void {
    if (this.eventQueue.length === 0) return;

    const queue = this.eventQueue;
    this.eventQueue = [];
    queue.forEach(({ event, providers, adapterType, adaptionData }) =>
      this.track(event, providers, adaptionData, adapterType),
    );
  }

  /**
   * 获取目标Provider列表
   */
  private getTargetProviders(providerTypes?: string[]): Provider[] {
    const allProviders = this.registry.getAllProviders();

    // 如果没有指定类型或传入空数组，返回所有Provider
    if (!providerTypes || providerTypes.length === 0) {
      return allProviders;
    }

    // 过滤指定类型的Provider
    return allProviders.filter((provider) => providerTypes.includes(provider.type));
  }
}
